import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  User,
  ChevronRight,
  X,
  Hash,
  CreditCard,
  RefreshCw,
  HelpCircle,
  Plus,
} from "lucide-react";
import { useTranslation, useLocale } from "../contexts/LocaleContext";
import { authenticatedFetch } from "../config/api";
import { getSortedCurrencies } from "../utils/currencyLocalization";
import { languages } from "../constants";
import { sanitizeInput } from "../utils/sanitize";
import SaveButton from "./SaveButton";
import Toggle from "../Toggle";
import SearchableDropdown from "./SearchableDropdown";
import InvoiceNumberTemplate from "../InvoiceNumberTemplate";
import PaymentMethodDialog from "../PaymentMethodDialog";

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSellerSettingsOpen?: () => void;
  showSellerSettings?: boolean;
}

export default function SettingsDialog({
  isOpen,
  onClose,
  onSellerSettingsOpen,
  showSellerSettings = false,
}: SettingsDialogProps) {
  const t = useTranslation();
  const { locale } = useLocale();
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);
  const [activeTab, setActiveTab] = useState<"general" | "seller">(
    showSellerSettings ? "seller" : "general",
  );

  const [showNumberTemplateModal, setShowNumberTemplateModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(showSellerSettings ? "seller" : "general");
      loadSavedPaymentMethods();
    }
  }, [isOpen, showSellerSettings]);

  const loadSavedPaymentMethods = async () => {
    try {
      const response = await authenticatedFetch("/api/payment-methods");
      if (response.ok) {
        const data = await response.json();
        setSavedPaymentMethods(data);
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
    }
  };

  const [settings, setSettings] = useState({
    defaultDueDays: 14,
    defaultCurrency: "CZK",
    defaultLanguage: "cs",
    invoiceNumberTemplate: "INV-{YYYY}-{MM}-{seq:4}",
    defaultTemplate: "Стандартный",
    invoiceStyle: "ordinary",
    defaultPaymentMethodId: "" as string | null,
    sellerName: "",
    sellerAddress: "",
    sellerTaxId: "",
    sellerDic: "",
    sellerWebsite: "",
    sellerEmail: "",
    sellerIsVatPayer: null as boolean | null,
    sellerIsCzechSoleProprietor: false,
    sellerNotes: "",
    sellerCountry: "",
  });

  const [customPresets, setCustomPresets] = useState<any[]>([]);
  const [aresLoading, setAresLoading] = useState(false);
  const [aresStatus, setAresStatus] = useState<
    "idle" | "success" | "not_found" | "error"
  >("idle");
  const [showCzechSoleProprietorTooltip, setShowCzechSoleProprietorTooltip] =
    useState(false);

  const isValidIco = (ico: string) => /^\d{8}$/.test(ico || "");

  const handleAresLookup = async () => {
    const ico = settings.sellerTaxId || "";
    if (!isValidIco(ico)) {
      setAresStatus("error");
      return;
    }

    setAresLoading(true);
    setAresStatus("idle");

    try {
      const response = await authenticatedFetch(`/api/utils/ares/${ico}`);
      if (response.status === 404) {
        setAresStatus("not_found");
        return;
      }
      if (!response.ok) throw new Error("ARES lookup failed");

      const data = await response.json();

      setSettings((prev) => ({
        ...prev,
        sellerName: data.obchodniJmeno || prev.sellerName,
        sellerAddress: data.sidlo?.textovaAdresa || prev.sellerAddress,
        sellerDic: data.dic || prev.sellerDic,
      }));

      setAresStatus("success");
    } catch (error) {
      console.error("ARES lookup error:", error);
      setAresStatus("error");
    } finally {
      setAresLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      loadCustomPresets();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    try {
      const response = await authenticatedFetch("/api/settings");
      if (response.ok) {
        const data = await response.json();
        setSettings((prev) => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  const loadCustomPresets = async () => {
    try {
      const response = await authenticatedFetch("/api/invoice-templates");
      if (response.ok) {
        const data = await response.json();
        setCustomPresets(
          data.map((p: any) => ({
            value: p.template,
            label: `Custom: ${p.template}`,
          })),
        );
      }
    } catch (error) {
      console.error("Error loading custom presets:", error);
    }
  };

  const handleSaveSettings = async () => {
    setIsLoadingSettings(true);
    try {
      // 1. Save Due Days
      const dueDaysRes = await authenticatedFetch(
        "/api/settings/default-due-days",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ value: settings.defaultDueDays }),
        },
      );

      if (!dueDaysRes.ok) throw new Error("Failed to save due days");

      // 2. Save Other Invoice Settings
      const invoiceSettingsRes = await authenticatedFetch(
        "/api/settings/invoice",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
          invoiceNumberTemplate: settings.invoiceNumberTemplate,
          defaultTemplate: settings.defaultTemplate,
          invoiceStyle: settings.invoiceStyle,
          defaultCurrency: settings.defaultCurrency,
          defaultLanguage: settings.defaultLanguage,
          defaultPaymentMethodId: settings.defaultPaymentMethodId,
          sellerName: settings.sellerName,
            sellerAddress: settings.sellerAddress,
            sellerTaxId: settings.sellerTaxId,
            sellerDic: settings.sellerDic,
            sellerWebsite: settings.sellerWebsite,
            sellerEmail: settings.sellerEmail,
            sellerIsVatPayer: settings.sellerIsVatPayer,
            sellerIsCzechSoleProprietor: settings.sellerIsCzechSoleProprietor,
            sellerNotes: settings.sellerNotes,
            sellerCountry: settings.sellerCountry,
          }),
        },
      );

      if (!invoiceSettingsRes.ok)
        throw new Error("Failed to save invoice settings");

      onClose();
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings");
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const getLabelClass = () =>
    "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 transition-colors";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200]"
          onMouseDown={(e) =>
            setMouseDownOnOverlay(e.target === e.currentTarget)
          }
          onMouseUp={(e) => {
            if (mouseDownOnOverlay && e.target === e.currentTarget) onClose();
            setMouseDownOnOverlay(false);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white dark:bg-slate-800 rounded-3xl p-5 md:p-8 max-w-2xl w-full mx-4 shadow-2xl max-h-[95vh] overflow-y-auto border border-slate-200/50 dark:border-slate-700/50 transition-colors"
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl flex items-center justify-center shadow-sm">
                  <Settings className="h-5 w-5 md:h-6 md:w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-slate-100 transition-colors">
                  {activeTab === "general"
                    ? t.invoiceList.settings.title
                    : t.invoiceList.settings.sellerModalTitle}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
              >
                <X className="h-5 w-5 md:h-6 md:w-6 text-slate-400" />
              </button>
            </div>

            <div className="space-y-3 md:space-y-4">
              {activeTab === "general" ? (
                <>
                  {onSellerSettingsOpen && (
                    <div className="pb-3 md:pb-4 border-b border-slate-200 dark:border-slate-700">
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => setActiveTab("seller")}
                        className="w-full flex items-center justify-between p-3 md:p-4 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-2xl text-purple-700 dark:text-purple-400 font-black transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shadow-sm text-purple-600 dark:text-purple-400">
                            <User className="h-4 w-4 md:h-5 md:w-5" />
                          </div>
                          <span className="text-xs sm:text-base tracking-tight">
                            {t.invoiceList.settings.configureMyData}
                          </span>
                        </div>
                        <ChevronRight className="h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" />
                      </motion.button>
                    </div>
                  )}

                  <div>
                    <label className={getLabelClass()}>
                      {t.invoiceList.settings.defaultCurrency}
                    </label>
                    <select
                      value={settings.defaultCurrency}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          defaultCurrency: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 shadow-sm transition-all duration-200 iv-select bg-white dark:bg-slate-700 dark:text-white text-sm rounded-xl"
                    >
                      {getSortedCurrencies(locale).map(
                        ({ key, code, label }) => (
                          <option key={key} value={code}>
                            {label}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label className={getLabelClass()}>
                        {t.invoiceList.settings.defaultLanguage ||
                          "Default Language"}
                      </label>
                      <select
                        value={settings.defaultLanguage}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            defaultLanguage: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 shadow-sm transition-all duration-200 iv-select bg-white dark:bg-slate-700 dark:text-white text-sm rounded-xl"
                      >
                        {Object.entries(languages).map(([code, lang]) => (
                          <option key={code} value={code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={getLabelClass()}>
                        {t.invoiceList.settings.defaultDueDays}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={settings.defaultDueDays}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            defaultDueDays: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 bg-white dark:bg-slate-700 dark:text-white shadow-sm transition-all duration-200 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className={getLabelClass()}>
                        {t.invoice.selectPaymentMethod}
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPaymentMethodModal(true)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest transition-colors shadow-sm"
                      >
                        <Plus size={10} />
                        {t.common.add}
                      </button>
                    </div>
                    <SearchableDropdown
                      options={savedPaymentMethods.map((method) => ({
                        value: method.id,
                        label: method.title,
                        sublabel:
                          method.type === "bank"
                            ? t.invoice.bankTransfer
                            : method.type === "link"
                              ? t.invoice.paymentLink
                              : method.type === "cash"
                                ? t.invoice.cash
                                : t.invoice.custom,
                      }))}
                      value={settings.defaultPaymentMethodId || ""}
                      onChange={(val) =>
                        setSettings({ ...settings, defaultPaymentMethodId: val })
                      }
                      placeholder={
                        t.invoice.selectPaymentMethod || t.common.select
                      }
                      searchPlaceholder={t.common.search || "Search..."}
                      noOptionsText={
                        t.invoice.noSavedMethods || "No methods found"
                      }
                      iconType="payment"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className={getLabelClass()}>
                        {t.invoiceList.settings.invoiceNumberTemplate}
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowNumberTemplateModal(true)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest transition-colors shadow-sm"
                      >
                        <Plus size={10} />
                        {t.common.add}
                      </button>
                    </div>
                    <select
                      value={settings.invoiceNumberTemplate}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          invoiceNumberTemplate: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 shadow-sm transition-all duration-200 iv-select bg-white dark:bg-slate-700 dark:text-white text-sm rounded-xl"
                    >
                      <option value="INV-{YYYY}-{MM}-{seq:4}">
                        INV-{`{YYYY}`}-{`{MM}`}-{`{seq:4}`}
                      </option>
                      <option value="{customer}-{YY}{MM}-{seq:3}">
                        {`{customer}`}-{`{YY}`}
                        {`{MM}`}-{`{seq:3}`}
                      </option>
                      <option value="{project}-{DD}{MM}{YYYY}-{seq}">
                        {`{project}`}-{`{DD}`}
                        {`{MM}`}
                        {`{YYYY}`}-{`{seq}`}
                      </option>
                      <option value="INV/{YYYY}/{MM}/{DD}/{seq:5}">
                        INV/{`{YYYY}`}/{`{MM}`}/{`{DD}`}/{`{seq:5}`}
                      </option>
                      {customPresets.map((p) => (
                        <option key={p.value} value={p.value}>
                          Custom: {p.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className={getLabelClass()}>{t.common.name}</label>
                    <input
                      value={settings.sellerName || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          sellerName: sanitizeInput(e.target.value),
                        })
                      }
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/10 space-y-2">
                      <label className={getLabelClass()}>
                        {t.invoice.identificationNumber}
                      </label>
                      <div className="space-y-2">
                        <div className="relative">
                          <Hash className="absolute left-3 top-2 h-4 w-4 text-slate-400" />
                          <input
                            value={settings.sellerTaxId || ""}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                sellerTaxId: sanitizeInput(e.target.value),
                              })
                            }
                            className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400"
                            placeholder="17653223"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAresLookup}
                          className="w-full py-1.5 px-3 rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-700 text-white text-[9px] font-black uppercase tracking-widest hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                          disabled={aresLoading}
                        >
                          {aresLoading ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : null}
                          {aresLoading
                            ? t.invoice.validation.aresLoading
                            : t.invoice.validation.aresButton}
                        </button>
                        {aresStatus !== "idle" && (
                          <div
                            className={`text-[9px] font-medium px-1.5 py-0.5 rounded-md inline-block ${
                              aresStatus === "success"
                                ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                                : aresStatus === "error" &&
                                    !isValidIco(settings.sellerTaxId)
                                  ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20"
                                  : "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20"
                            }`}
                          >
                            {aresStatus === "success" && t.common.success}
                            {aresStatus === "error" &&
                              !isValidIco(settings.sellerTaxId) &&
                              t.invoice.validation.aresMinChars}
                            {aresStatus === "not_found" &&
                              t.invoice.validation.aresNotFound}
                            {aresStatus === "error" &&
                              isValidIco(settings.sellerTaxId) &&
                              t.invoice.validation.aresError}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className={getLabelClass()}>{t.invoice.dic}</label>
                      <div className="relative">
                        <CreditCard className="absolute left-3 top-2 h-4 w-4 text-slate-400" />
                        <input
                          value={settings.sellerDic || ""}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              sellerDic: sanitizeInput(e.target.value),
                            })
                          }
                          className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100"
                          placeholder="DIČ"
                        />
                      </div>
                      <div className="pt-1.5">
                        <div className="flex flex-col gap-1.5 p-2.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                            {t.invoice.vatPayer}
                          </span>
                          <select
                            value={
                              settings.sellerIsVatPayer === null
                                ? ""
                                : String(settings.sellerIsVatPayer)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              setSettings({
                                ...settings,
                                sellerIsVatPayer:
                                  val === "" ? null : val === "true",
                              });
                            }}
                            className="w-full px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[11px] text-slate-900 dark:text-slate-100 iv-select"
                          >
                            <option value="">{t.invoice.notSpecified}</option>
                            <option value="true">
                              {t.invoice.vatPayer.replace("?", "")}
                            </option>
                            <option value="false">
                              {t.invoice.nonVatPayer}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div className="pt-1.5">
                        <div className="p-2.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                {t.invoice.czechSoleProprietor}
                              </span>
                              <div
                                className="relative flex items-center"
                                onMouseEnter={() =>
                                  setShowCzechSoleProprietorTooltip(true)
                                }
                                onMouseLeave={() =>
                                  setShowCzechSoleProprietorTooltip(false)
                                }
                              >
                                <HelpCircle
                                  size={12}
                                  className="text-slate-400 cursor-help"
                                />
                                <AnimatePresence>
                                  {showCzechSoleProprietorTooltip && (
                                    <motion.div
                                      initial={{
                                        opacity: 0,
                                        y: 10,
                                        scale: 0.95,
                                      }}
                                      animate={{ opacity: 1, y: 0, scale: 1 }}
                                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-[10px] font-bold rounded-xl shadow-xl z-50 pointer-events-none w-64 text-center border border-slate-700/50"
                                    >
                                      {t.invoice.czechSoleProprietorTooltip}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </div>
                            <Toggle
                              checked={
                                settings.sellerIsCzechSoleProprietor || false
                              }
                              onChange={(checked) =>
                                setSettings({
                                  ...settings,
                                  sellerIsCzechSoleProprietor: checked,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className={getLabelClass()}>
                      {t.invoice.address}
                    </label>
                    <textarea
                      value={settings.sellerAddress || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          sellerAddress: sanitizeInput(e.target.value),
                        })
                      }
                      className="w-full px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm min-h-[50px] text-slate-900 dark:text-slate-100"
                      placeholder="Address"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className={getLabelClass()}>
                        {t.invoice.website}
                      </label>
                      <input
                        value={settings.sellerWebsite || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            sellerWebsite: sanitizeInput(e.target.value),
                          })
                        }
                        className="w-full px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100"
                        placeholder="https://example.com"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className={getLabelClass()}>
                        {t.clientDialog.email}
                      </label>
                      <input
                        type="email"
                        value={settings.sellerEmail || ""}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            sellerEmail: sanitizeInput(e.target.value),
                          })
                        }
                        className="w-full px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400"
                        placeholder="Email"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className={getLabelClass()}>{t.invoice.notes}</label>
                    <textarea
                      value={settings.sellerNotes || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          sellerNotes: sanitizeInput(e.target.value),
                        })
                      }
                      className="w-full px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm min-h-[50px] text-slate-900 dark:text-slate-100"
                      placeholder="Notes"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 md:gap-4 mt-3 pt-3 md:mt-6 md:pt-4 border-t border-slate-200 dark:border-slate-700">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={
                  activeTab === "seller"
                    ? () => setActiveTab("general")
                    : onClose
                }
                className="flex-1 px-4 py-2 md:px-6 md:py-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 font-semibold transition-all duration-200 shadow-sm text-sm"
              >
                {activeTab === "seller" ? t.common.back : t.common.cancel}
              </motion.button>
              <SaveButton
                onClick={handleSaveSettings}
                isSaving={isLoadingSettings}
                text={t.common.save}
                className="flex-1 px-4 py-2 md:px-6 md:py-3"
              />
            </div>
          </motion.div>
        </motion.div>
      )}

      <InvoiceNumberTemplate
        isOpen={showNumberTemplateModal}
        onClose={() => setShowNumberTemplateModal(false)}
        onSave={async (template) => {
          setSettings((prev) => ({ ...prev, invoiceNumberTemplate: template }));
          await loadCustomPresets();
        }}
        onApply={(template) => {
          setSettings((prev) => ({ ...prev, invoiceNumberTemplate: template }));
        }}
        customPresets={customPresets.map((p) => ({
          name: p.label,
          value: p.value,
        }))}
        onDeletePreset={async (template) => {
          try {
            const encodedTemplate = encodeURIComponent(template);
            await authenticatedFetch(
              `/api/invoice-templates/presets/${encodedTemplate}`,
              { method: "DELETE" },
            );
            await loadCustomPresets();
          } catch (error) {
            console.error(error);
          }
        }}
        zIndex={300}
      />

      <PaymentMethodDialog
        isOpen={showPaymentMethodModal}
        onClose={() => setShowPaymentMethodModal(false)}
        paymentMethods={savedPaymentMethods}
        onAddPaymentMethod={async (method) => {
          try {
            const response = await authenticatedFetch("/api/payment-methods", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(method),
            });
            if (response.ok) {
              await loadSavedPaymentMethods();
              setShowPaymentMethodModal(false);
            }
          } catch (error) {
            console.error(error);
          }
        }}
        onUpdatePaymentMethod={async (id, method) => {
          try {
            const response = await authenticatedFetch(
              `/api/payment-methods/${id}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(method),
              },
            );
            if (response.ok) {
              await loadSavedPaymentMethods();
            }
          } catch (error) {
            console.error(error);
          }
        }}
        onDeletePaymentMethod={async (id) => {
          try {
            await authenticatedFetch(`/api/payment-methods/${id}`, {
              method: "DELETE",
            });
            await loadSavedPaymentMethods();
          } catch (error) {
            console.error(error);
          }
        }}
        zIndex={300}
      />
    </AnimatePresence>
  );
}
